import type { MouseEventHandler } from "react";
import { useEffect, useCallback, useRef } from "react";

import { defineMessages, useIntl } from "react-intl";

import classNames from "classnames";
import { useLocation } from "react-router-dom";

import type { Map as ImmutableMap } from "immutable";

import { animated, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";

import { useAccount } from "@/flavours/glitch/hooks/useAccount";
import AddIcon from "@/material-icons/400-24px/add.svg?react";
import AlternateEmailIcon from "@/material-icons/400-24px/alternate_email.svg?react";
import MailIcon from "@/material-icons/400-24px/mail.svg?react";
import ArticleIcon from "@/material-icons/400-24px/article.svg?react";
import BookmarksActiveIcon from "@/material-icons/400-24px/bookmarks-fill.svg?react";
import BookmarksIcon from "@/material-icons/400-24px/bookmarks.svg?react";
import CollectionsActiveIcon from "@/material-icons/400-24px/category-fill.svg?react";
import CollectionsIcon from "@/material-icons/400-24px/category.svg?react";
import HelpIcon from "@/material-icons/400-24px/help.svg?react";
import HomeActiveIcon from "@/material-icons/400-24px/home-fill.svg?react";
import HomeIcon from "@/material-icons/400-24px/home.svg?react";
import DescriptionIcon from "@/material-icons/400-24px/description.svg?react";
import EditNoteIcon from "@/material-icons/400-24px/edit_note.svg?react";
import GavelIcon from "@/material-icons/400-24px/gavel.svg?react";
import AdministrationIcon from "@/material-icons/400-24px/manufacturing.svg?react";
import NotificationsActiveIcon from "@/material-icons/400-24px/notifications-fill.svg?react";
import NotificationsIcon from "@/material-icons/400-24px/notifications.svg?react";
import PersonAddActiveIcon from "@/material-icons/400-24px/person_add-fill.svg?react";
import PersonAddIcon from "@/material-icons/400-24px/person_add.svg?react";
import PublicIcon from "@/material-icons/400-24px/public.svg?react";
import SearchIcon from "@/material-icons/400-24px/search.svg?react";
import SettingsIcon from "@/material-icons/400-24px/settings.svg?react";
import StarActiveIcon from "@/material-icons/400-24px/star-fill.svg?react";
import StarIcon from "@/material-icons/400-24px/star.svg?react";
import TrendingUpIcon from "@/material-icons/400-24px/trending_up.svg?react";
import CelebrationIcon from "@/material-icons/400-24px/celebration.svg?react";
import GroupsIcon from "@/material-icons/400-24px/groups.svg?react";
import TagIcon from "@/material-icons/400-24px/tag.svg?react";
import TripIcon from "@/material-icons/400-24px/trip.svg?react";
import { fetchFollowRequests } from "flavours/glitch/actions/accounts";
import { openModal } from "flavours/glitch/actions/modal";
import { openNavigation, closeNavigation } from "flavours/glitch/actions/navigation";
import { Account } from "flavours/glitch/components/account";
import { IconWithBadge } from "flavours/glitch/components/icon_with_badge";
import { ColumnLink } from "flavours/glitch/features/ui/components/column_link";
import { getNavigationSkipLinkId } from "flavours/glitch/features/ui/components/skip_links";
import { useBreakpoint } from "flavours/glitch/features/ui/hooks/useBreakpoint";
import { useIdentity } from "flavours/glitch/identity_context";
import {
  localLiveFeedAccess,
  remoteLiveFeedAccess,
  trendsEnabled,
  me,
} from "flavours/glitch/initial_state";
import { transientSingleColumn } from "flavours/glitch/is_mobile";
import { canViewFeed } from "flavours/glitch/permissions";
import { selectUnreadNotificationGroupsCount } from "flavours/glitch/selectors/notifications";
import { useAppSelector, useAppDispatch } from "flavours/glitch/store";

import { AnnualReportNavItem } from "../annual_report/nav_item";

import { CheckForUpdatesButton } from "./components/check_for_updates_button";
import { DisabledAccountBanner } from "./components/disabled_account_banner";
import { FollowedTagsPanel } from "./components/followed_tags_panel";
import { ListPanel } from "./components/list_panel";
import { MoreLink } from "./components/more_link";
import { SignInBanner } from "./components/sign_in_banner";
import { Trends } from "./components/trends";
import { LanguageSwitcher } from "flavours/glitch/components/language_switcher";

const messages = defineMessages({
  home: { id: "tabs_bar.home", defaultMessage: "Home" },
  notifications: {
    id: "tabs_bar.notifications",
    defaultMessage: "Notifications",
  },
  explore: { id: "explore.title", defaultMessage: "Trending" },
  firehose: { id: "column.firehose", defaultMessage: "Live feeds" },
  firehose_singular: {
    id: "column.firehose_singular",
    defaultMessage: "Live feed",
  },
  main: {
    id: "navigation_bar.main",
    defaultMessage: "Main",
    description: 'Label for the main navigation; should not contain the word "navigation".',
  },
  direct: { id: "navigation_bar.direct", defaultMessage: "Private mentions" },
  favourites: { id: "navigation_bar.favourites", defaultMessage: "Favorites" },
  bookmarks: { id: "navigation_bar.bookmarks", defaultMessage: "Bookmarks" },
  collections: {
    id: "navigation_bar.collections",
    defaultMessage: "Collections",
  },
  preferences: {
    id: "navigation_bar.preferences",
    defaultMessage: "Preferences",
  },
  followsAndFollowers: {
    id: "navigation_bar.follows_and_followers",
    defaultMessage: "Follows and followers",
  },
  search: { id: "navigation_bar.search", defaultMessage: "Search" },
  searchTrends: {
    id: "navigation_bar.search_trends",
    defaultMessage: "Search / Trending",
  },
  advancedInterface: {
    id: "navigation_bar.advanced_interface",
    defaultMessage: "Open in advanced web interface",
  },
  openedInClassicInterface: {
    id: "navigation_bar.opened_in_classic_interface",
    defaultMessage:
      "Posts, accounts, and other specific pages are opened by default in the classic web interface.",
  },
  followRequests: {
    id: "navigation_bar.follow_requests",
    defaultMessage: "Follow requests",
  },
  logout: { id: "navigation_bar.logout", defaultMessage: "Logout" },
  communityVisits: { id: "navigation_bar.community_visits", defaultMessage: "When I'm In Town" },
  communityEvents: { id: "navigation_bar.community_events", defaultMessage: "Community Events" },
  communityListings: { id: "navigation_bar.community_listings", defaultMessage: "Listings" },
  memberStories: { id: "navigation_bar.member_stories", defaultMessage: "Member Stories" },
  notificationSettings: {
    id: "navigation_bar.notification_settings",
    defaultMessage: "Notification Settings",
  },
  communityNotifications: {
    id: "navigation_bar.community_notifications",
    defaultMessage: "Community Notifications",
  },
  visitsAdmin: { id: "navigation_bar.visits_admin", defaultMessage: "Visits Admin" },
  compose: { id: "tabs_bar.publish", defaultMessage: "New Post" },
  contactAdmin: { id: "navigation_bar.contact_admin", defaultMessage: "Contact Admin" },
  dailyDigest: { id: "navigation_bar.daily_digest", defaultMessage: "Daily Digest" },
  howItWorks: { id: "navigation_bar.how_it_works", defaultMessage: "How It Works" },
  app_settings: {
    id: "navigation_bar.app_settings",
    defaultMessage: "App settings",
  },
});

const NotificationsLink = () => {
  const count = useAppSelector(selectUnreadNotificationGroupsCount);
  const showCount = useAppSelector(
    (state) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      state.local_settings.getIn(["notifications", "tab_badge"]) as boolean,
  );
  const intl = useIntl();

  return (
    <ColumnLink
      key="notifications"
      transparent
      to="/notifications"
      icon={
        <IconWithBadge
          id="bell"
          icon={NotificationsIcon}
          count={showCount ? count : 0}
          className="column-link__icon"
        />
      }
      activeIcon={
        <IconWithBadge
          id="bell"
          icon={NotificationsActiveIcon}
          count={showCount ? count : 0}
          className="column-link__icon"
        />
      }
      text={intl.formatMessage(messages.notifications)}
    />
  );
};

const FollowRequestsLink: React.FC = () => {
  const intl = useIntl();
  const count = useAppSelector(
    (state) =>
      (
        state.user_lists.getIn(["follow_requests", "items"]) as
          | ImmutableMap<string, unknown>
          | undefined
      )?.size ?? 0,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchFollowRequests());
  }, [dispatch]);

  if (count === 0) {
    return null;
  }

  return (
    <ColumnLink
      transparent
      to="/follow_requests"
      icon={
        <IconWithBadge
          id="user-plus"
          icon={PersonAddIcon}
          count={count}
          className="column-link__icon"
        />
      }
      activeIcon={
        <IconWithBadge
          id="user-plus"
          icon={PersonAddActiveIcon}
          count={count}
          className="column-link__icon"
        />
      }
      text={intl.formatMessage(messages.followRequests)}
    />
  );
};

const ProfileCard: React.FC = () => {
  if (!me) {
    return null;
  }

  return (
    <div className="navigation-bar">
      <Account id={me} minimal size={36} />
    </div>
  );
};

const isFirehoseActive = (match: unknown, { pathname }: { pathname: string }) => {
  return !!match || pathname.startsWith("/public");
};

const MENU_WIDTH = 284;

export const NavigationPanel: React.FC<{ multiColumn?: boolean }> = ({ multiColumn = false }) => {
  const intl = useIntl();
  const { signedIn, permissions, disabledAccountId } = useIdentity();
  const location = useLocation();
  const account = useAccount(me);
  const dispatch = useAppDispatch();

  let banner: React.ReactNode;

  let linknum = 0;

  if (transientSingleColumn) {
    banner = (
      <div className="switch-to-advanced">
        {intl.formatMessage(messages.openedInClassicInterface)}{" "}
        <a href={`/deck${location.pathname}`} className="switch-to-advanced__toggle">
          {intl.formatMessage(messages.advancedInterface)}
        </a>
      </div>
    );
  }

  const handleOpenSettings = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      dispatch(
        openModal({
          modalType: "SETTINGS",
          modalProps: {},
        }),
      );
    },
    [dispatch],
  );

  return (
    <nav className="navigation-panel" aria-label={intl.formatMessage(messages.main)}>
      {!multiColumn && <ProfileCard />}

      {banner && <div className="navigation-panel__banner">{banner}</div>}

      <div className="navigation-panel__menu">
        <div className="navigation-panel__language-switcher">
          <LanguageSwitcher />
        </div>

        {/* Login / Join — compact buttons at the top for visitors */}
        {!signedIn && (
          <div className="navigation-panel__sign-in-banner">
            {disabledAccountId ? <DisabledAccountBanner /> : <SignInBanner />}
          </div>
        )}

        {/* ── Primary community nav — visible to everyone ── */}
        <ColumnLink
          transparent
          to="/"
          icon="home"
          iconComponent={HomeIcon}
          activeIconComponent={HomeActiveIcon}
          text={intl.formatMessage(messages.home)}
          id={linknum++ === 0 ? getNavigationSkipLinkId() : undefined}
        />
        <ColumnLink
          transparent
          to="/guide"
          icon="help"
          iconComponent={HelpIcon}
          text={intl.formatMessage(messages.howItWorks)}
        />
        <ColumnLink
          transparent
          to="/landing"
          icon="category"
          iconComponent={CollectionsIcon}
          text="Community"
        />
        <ColumnLink
          transparent
          to="/community_visits"
          icon="trip"
          iconComponent={TripIcon}
          text={intl.formatMessage(messages.communityVisits)}
        />
        <ColumnLink
          transparent
          to="/community_events"
          icon="celebration"
          iconComponent={CelebrationIcon}
          text={intl.formatMessage(messages.communityEvents)}
        />
        <ColumnLink
          transparent
          to="/community_listings"
          icon="tag"
          iconComponent={TagIcon}
          text={intl.formatMessage(messages.communityListings)}
        />
        <ColumnLink
          transparent
          to="/shared"
          icon="description"
          iconComponent={DescriptionIcon}
          text="Barbara & David"
        />
        <ColumnLink
          transparent
          to="/daily"
          icon="article"
          iconComponent={ArticleIcon}
          text={intl.formatMessage(messages.dailyDigest)}
        />

        {/* Manual "is there a newer version?" check -- visible to everyone,
            since a phone's home-screen app can sit on a stale copy for days. */}
        <div className="navigation-panel__check-updates">
          <CheckForUpdatesButton />
        </div>

        {signedIn && (
          <ColumnLink
            transparent
            to="/member_stories"
            icon="groups"
            iconComponent={GroupsIcon}
            text={intl.formatMessage(messages.memberStories)}
          />
        )}

        {signedIn && (
          <ColumnLink
            transparent
            to="/community_notifications"
            icon="notifications"
            iconComponent={NotificationsIcon}
            text={intl.formatMessage(messages.communityNotifications)}
          />
        )}

        {signedIn && (
          <ColumnLink
            transparent
            to="/notification_settings"
            icon="settings"
            iconComponent={SettingsIcon}
            text={intl.formatMessage(messages.notificationSettings)}
          />
        )}

        {signedIn && !!(permissions & 0x1) && (
          <>
            <hr />
            <div className="navigation-panel__admin-heading">Admin Tools</div>
            <ColumnLink
              transparent
              id="admin-quick-search-link"
              href="/admin/quick_search"
              icon="search"
              iconComponent={SearchIcon}
              text="Admin Quick Search"
            />
            <ColumnLink
              transparent
              href="/admin/site_settings/edit"
              icon="edit_note"
              iconComponent={EditNoteIcon}
              text="Site Content & Translations"
            />
            <ColumnLink
              transparent
              to="/community_directory/admin"
              icon="category"
              iconComponent={CollectionsIcon}
              text="Community Admin"
            />
            <ColumnLink
              transparent
              to="/community_directory/moderation"
              icon="gavel"
              iconComponent={GavelIcon}
              text="Moderation Queue"
            />
            <ColumnLink
              transparent
              to="/community_directory/scraper_logs"
              icon="public"
              iconComponent={PublicIcon}
              text="Scraper Logs"
            />
            <ColumnLink
              transparent
              to="/community_maintenance"
              icon="manufacturing"
              iconComponent={AdministrationIcon}
              text="Site Maintenance"
            />
            <ColumnLink
              transparent
              to="/community_visits/admin"
              icon="manage_accounts"
              text="Visits Admin"
            />
            <ColumnLink
              transparent
              href="/admin/dashboard"
              icon="settings"
              iconComponent={SettingsIcon}
              text="Mastodon Admin"
            />
          </>
        )}

        {/* Newsletters: administrator (0x1) or manage_reports (0x10) --
            matches Admin::NewslettersController#require_admin!. Kept as
            its own block (not folded into Admin Tools above) so
            Moderator-role accounts (e.g. Barbara) see it too, without
            opening up the rest of Admin Tools to them. Previously this
            page had no link anywhere in the app at all, for any role. */}
        {signedIn && !!(permissions & 0x11) && (
          <>
            <hr />
            <div className="navigation-panel__admin-heading">Newsletters</div>
            <ColumnLink
              transparent
              href="/admin/newsletters"
              icon="description"
              iconComponent={DescriptionIcon}
              text="Newsletters Admin"
            />
            <ColumnLink
              transparent
              to="/shared/new"
              icon="description"
              iconComponent={DescriptionIcon}
              text="Quick Share a PDF"
            />
          </>
        )}

        {/* ── Social features — signed-in only ── */}
        {signedIn && (
          <>
            <hr />

            {!multiColumn && (
              <ColumnLink
                to={{ pathname: "/publish", state: { focusTarget: false } }}
                icon="plus"
                iconComponent={AddIcon}
                activeIconComponent={AddIcon}
                text={intl.formatMessage(messages.compose)}
                className="button navigation-panel__compose-button"
              />
            )}

            <NotificationsLink />

            <FollowRequestsLink />

            <AnnualReportNavItem />

            {trendsEnabled && (
              <ColumnLink
                transparent
                to="/explore"
                icon="explore"
                iconComponent={TrendingUpIcon}
                text={intl.formatMessage(messages.explore)}
              />
            )}

            {(canViewFeed(signedIn, permissions, localLiveFeedAccess) ||
              canViewFeed(signedIn, permissions, remoteLiveFeedAccess)) && (
              <ColumnLink
                transparent
                to={
                  canViewFeed(signedIn, permissions, localLiveFeedAccess)
                    ? "/public/local"
                    : "/public/remote"
                }
                icon="globe"
                iconComponent={PublicIcon}
                isActive={isFirehoseActive}
                text={intl.formatMessage(
                  canViewFeed(signedIn, permissions, localLiveFeedAccess) &&
                    canViewFeed(signedIn, permissions, remoteLiveFeedAccess)
                    ? messages.firehose
                    : messages.firehose_singular,
                )}
              />
            )}

            <hr />

            <ListPanel />

            <FollowedTagsPanel />

            <ColumnLink
              transparent
              to="/favourites"
              icon="star"
              iconComponent={StarIcon}
              activeIconComponent={StarActiveIcon}
              text={intl.formatMessage(messages.favourites)}
            />
            <ColumnLink
              transparent
              to="/bookmarks"
              icon="bookmarks"
              iconComponent={BookmarksIcon}
              activeIconComponent={BookmarksActiveIcon}
              text={intl.formatMessage(messages.bookmarks)}
            />
            <ColumnLink
              transparent
              to={`/@${account?.acct}/collections`}
              icon="collections"
              iconComponent={CollectionsIcon}
              activeIconComponent={CollectionsActiveIcon}
              text={intl.formatMessage(messages.collections)}
            />
            <ColumnLink
              transparent
              to="/conversations"
              icon="at"
              iconComponent={AlternateEmailIcon}
              text={intl.formatMessage(messages.direct)}
            />

            <hr />

            <ColumnLink
              transparent
              href="/settings/preferences"
              icon="cog"
              iconComponent={SettingsIcon}
              text={intl.formatMessage(messages.preferences)}
            />
            <ColumnLink
              transparent
              onClick={handleOpenSettings}
              icon="cogs"
              iconComponent={AdministrationIcon}
              text={intl.formatMessage(messages.app_settings)}
            />

            <ColumnLink
              transparent
              to="/contact"
              icon="mail"
              iconComponent={MailIcon}
              text={intl.formatMessage(messages.contactAdmin)}
            />

            <MoreLink />
          </>
        )}
      </div>

      <div className="flex-spacer" />

      <Trends />
    </nav>
  );
};

export const CollapsibleNavigationPanel: React.FC = () => {
  const open = useAppSelector((state) => state.navigation.open);
  const dispatch = useAppDispatch();
  const openable = useBreakpoint("openable");
  const location = useLocation();
  const overlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(closeNavigation());
  }, [dispatch, location]);

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (overlayRef.current && e.target === overlayRef.current) {
        dispatch(closeNavigation());
      }
    };

    const handleDocumentKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dispatch(closeNavigation());
      }
    };

    document.addEventListener("click", handleDocumentClick);
    document.addEventListener("keyup", handleDocumentKeyUp);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      document.removeEventListener("keyup", handleDocumentKeyUp);
    };
  }, [dispatch]);

  const isLtrDir = getComputedStyle(document.body).direction !== "rtl";

  const OPEN_MENU_OFFSET = isLtrDir ? MENU_WIDTH : -MENU_WIDTH;

  const [{ x }, spring] = useSpring(
    () => ({
      x: open ? 0 : OPEN_MENU_OFFSET,
      onRest: {
        x({ value }: { value: number }) {
          if (value === 0) {
            dispatch(openNavigation());
          } else if (value === OPEN_MENU_OFFSET) {
            dispatch(closeNavigation());
          }
        },
      },
    }),
    [open],
  );

  const bind = useDrag(
    ({ last, offset: [xOffset], velocity: [xVelocity], direction: [xDirection], cancel }) => {
      const logicalXDirection = isLtrDir ? xDirection : -xDirection;
      const logicalXOffset = isLtrDir ? xOffset : -xOffset;
      const hasReachedDragThreshold = logicalXOffset < -70;

      if (hasReachedDragThreshold) {
        cancel();
      }

      if (last) {
        const isAboveOpenThreshold = logicalXOffset > MENU_WIDTH / 2;
        const isQuickFlick = xVelocity > 0.5 && logicalXDirection > 0;

        if (isAboveOpenThreshold || isQuickFlick) {
          void spring.start({ x: OPEN_MENU_OFFSET });
        } else {
          void spring.start({ x: 0 });
        }
      } else {
        void spring.start({ x: xOffset, immediate: true });
      }
    },
    {
      from: () => [x.get(), 0],
      axis: "x",
      filterTaps: true,
      bounds: isLtrDir ? { left: 0 } : { right: 0 },
      rubberband: true,
      enabled: openable,
    },
  );

  const previouslyFocusedElementRef = useRef<HTMLElement | null>();

  useEffect(() => {
    if (open) {
      const firstLink = document.querySelector<HTMLAnchorElement>(
        ".navigation-panel__menu .column-link",
      );
      previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
      firstLink?.focus();
    } else {
      previouslyFocusedElementRef.current?.focus();
    }
  }, [open]);

  const showOverlay = openable && open;

  return (
    <div
      className={classNames(
        "columns-area__panels__pane columns-area__panels__pane--start columns-area__panels__pane--navigational",
        { "columns-area__panels__pane--overlay": showOverlay },
      )}
      ref={overlayRef}
    >
      <animated.div
        className="columns-area__panels__pane__inner"
        {...bind()}
        style={openable ? { x } : undefined}
      >
        <NavigationPanel />
      </animated.div>
    </div>
  );
};
